import React, { forwardRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  TableCellsIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { cardClass, focusRing, linkButtonClass, Spinner } from "../../pages/Exams/TestBuilder/ui";
import { badgeClass } from "./variantUi";
import { bankHref, blockingLines, issueText, LEVELS, questionList, rowKey } from "./variantText";

const TOTAL_KEYS = [...LEVELS, "count", "points"];
const CHANGE_CODES = ["plan_changed", "question_changed"];

// Planned against actual: A / B / C, questions and points; a cell off its plan is marked.
function TotalsTable({ planned, actual }) {
  const [t] = useTranslation();
  const head = (key) => (LEVELS.includes(key) ? key : t(`variants.check.col_${key}`));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm tabular-nums">
        <caption className="sr-only">{t("variants.check.totals_caption")}</caption>
        <thead>
          <tr className="text-xs text-slate-500">
            <th scope="col" className="py-1.5 pr-2 text-left font-medium">
              <span className="sr-only">{t("variants.check.row_label")}</span>
            </th>
            {TOTAL_KEYS.map((key) => (
              <th key={key} scope="col" className="px-1.5 py-1.5 text-right font-semibold">
                {head(key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          <tr>
            <th scope="row" className="py-1.5 pr-2 text-left font-normal text-slate-500">
              {t("variants.check.planned")}
            </th>
            {TOTAL_KEYS.map((key) => (
              <td key={key} className="px-1.5 py-1.5 text-right text-slate-700">
                {planned?.[key] ?? 0}
              </td>
            ))}
          </tr>
          <tr>
            <th scope="row" className="py-1.5 pr-2 text-left font-normal text-slate-500">
              {t("variants.check.actual")}
            </th>
            {TOTAL_KEYS.map((key) => {
              const value = actual?.[key] ?? 0;
              const target = planned?.[key] ?? 0;
              const tone = value === target ? "text-slate-900" : value < target ? "text-amber-700" : "text-red-700";
              return (
                <td key={key} className={`px-1.5 py-1.5 text-right font-semibold ${tone}`}>
                  {value}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// A switch for one question's options: kept in their order in the booklet, or shuffled as usual. While its write
// runs it is aria-disabled (not disabled), so it keeps the keyboard focus. Phones get a 44px tall hit area.
function KeepOrderSwitch({ checked, label, disabled, busy, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      aria-disabled={busy || undefined}
      onClick={() => {
        if (!busy) onChange(!checked);
      }}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition duration-200 disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-wait aria-disabled:opacity-60 max-sm:after:absolute max-sm:after:inset-x-0 max-sm:after:-inset-y-2.5 max-sm:after:content-[''] ${focusRing} ${
        checked ? "bg-blue-600" : "bg-slate-300"
      }`}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}

/**
 * The check of the variant against its matrix: planned and actual totals, whether it is the BTS standard,
 * then what blocks the confirmation (red) and the warnings (amber) in plain words, the questions named by their
 * number. A week of the matrix without a topic blocks it too, and only the matrix can mend that: its line leads
 * there (`matrixHref`). A question whose options name other options can keep their order ("Do not shuffle the
 * options": keep_order). `refused`: the last confirmation was refused for these issues.
 */
const VariantCheck = forwardRef(function VariantCheck(
  { detail, refs, passages, folderUuid, matrixHref, readOnly, refused, keepBusy, onKeepOrder, onConfirmAgain, confirmBusy },
  ref
) {
  const [t] = useTranslation();
  const check = detail.check || {};
  const issues = useMemo(() => detail.check?.issues || [], [detail.check]);
  const changed = check.changed_since_confirmation || {};
  const changedSince = detail.status === "confirmed" && (changed.plan || changed.questions?.length > 0);
  // What changed since the confirmation has its own notice (with "Confirm again") above the list.
  const warnings = issues.filter((issue) => !issue.blocking && !(changedSince && CHANGE_CODES.includes(issue.code)));
  // An empty variant is short in every place: one line says so instead of one per place (and the badge counts lines).
  const { emptyShort, issues: shownBlocking, count: blockingCount } = blockingLines(check);
  const keep = useMemo(() => new Set(detail.keep_order || []), [detail]);
  const approvedOf = useMemo(() => new Map((check.rows || []).map((entry) => [rowKey(entry.row), entry.approved || {}])), [check.rows]);
  // Questions whose options name others, and those already kept in order (the check no longer lists them).
  const keepCandidates = useMemo(() => {
    const listed = issues.find((issue) => issue.code === "option_reference")?.questions || [];
    return [...new Set([...listed, ...(detail.keep_order || [])])]
      .filter((uuid) => refs.has(uuid))
      .sort((a, b) => refs.get(a).number - refs.get(b).number);
  }, [issues, detail.keep_order, refs]);
  const issueItem = (issue, index, tone) => {
    const Icon = tone === "red" ? XCircleIcon : ExclamationTriangleIcon;
    const short = issue.code === "row_short" && issue.row;
    const approved = short ? approvedOf.get(rowKey(issue.row))?.[issue.level] ?? 0 : null;
    const href = short ? bankHref(folderUuid, issue.row, issue.level) : null;
    return (
      <li key={`${issue.code}-${index}`} className="flex items-start gap-2 py-2">
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone === "red" ? "text-red-600" : "text-amber-500"}`} aria-hidden="true" />
        <div className="min-w-0 text-sm text-slate-700 [overflow-wrap:anywhere]">
          <p>{issueText(t, issue, { refs, passages })}</p>
          {short && (
            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
              {t("variants.check.approved_in_bank", { count: approved })}
              {href && (
                <a href={href} target="_blank" rel="noopener noreferrer" className={`${linkButtonClass} px-1 py-0 text-xs max-sm:min-h-11`}>
                  {t("variants.find_in_bank")}
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              )}
            </p>
          )}
          {issue.code === "row_without_topic" && matrixHref && (
            <Link to={matrixHref} className={`${linkButtonClass} -ml-1 mt-0.5 px-1 py-0 text-xs max-sm:min-h-11`}>
              <TableCellsIcon className="h-3.5 w-3.5" aria-hidden="true" />
              {t("variants.list.open_matrix")}
            </Link>
          )}
        </div>
      </li>
    );
  };

  return (
    <section ref={ref} tabIndex={-1} aria-labelledby="variant-check-title" className={`${cardClass} min-w-0 outline-none`}>
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
        <h4 id="variant-check-title" className="text-base font-semibold text-slate-900">
          {t("variants.check.title")}
        </h4>
        {check.ok ? (
          // A confirmed variant is no longer "ready to confirm": it passed the check.
          <span className={`${badgeClass} bg-emerald-50 text-emerald-700 ring-emerald-600/20`}>
            <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {t(detail.status === "confirmed" ? "variants.check.passed" : "variants.check.ok")}
          </span>
        ) : (
          // The header's dimmed Confirm has its own line that says why (VariantDetail); this one is the check's.
          <span className={`${badgeClass} bg-red-50 text-red-700 ring-red-600/20`}>
            <XCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {t("variants.check.blocking_count", { count: blockingCount })}
          </span>
        )}
      </header>

      <div className="space-y-4 px-4 py-3">
        {refused && !check.ok && (
          <p role="alert" className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            <XCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
            {t("variants.check.refused")}
          </p>
        )}
        {changedSince && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <p className="flex items-start gap-2">
              <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
              <span>
                {[
                  changed.plan ? t("variants.check.changed_plan") : null,
                  changed.questions?.length ? t("variants.check.changed_questions", { list: questionList(t, changed.questions, refs) }) : null,
                ]
                  .filter(Boolean)
                  .join(" ")}{" "}
                {t("variants.check.confirm_again_hint")}
              </span>
            </p>
            {!readOnly && (
              <button
                type="button"
                onClick={(e) => {
                  if (!confirmBusy) onConfirmAgain(e.currentTarget);
                }}
                aria-disabled={confirmBusy || undefined}
                className={`${linkButtonClass} mt-1 text-amber-900 aria-disabled:cursor-wait max-sm:min-h-11`}
              >
                {confirmBusy && <Spinner className="h-3.5 w-3.5" />}
                {t("variants.actions.confirm_again")}
              </button>
            )}
          </div>
        )}

        <TotalsTable planned={check.planned} actual={check.actual} />

        {check.standard === true && (
          <p className="flex items-start gap-2 text-sm text-emerald-800">
            <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
            {t("variants.check.standard_yes")}
          </p>
        )}
        {/* A "not standard" warning below says it with the plan's numbers. */}
        {check.standard === false && !warnings.some((issue) => issue.code === "not_standard") && (
          <p className="flex items-start gap-2 text-sm text-amber-900">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
            {t("variants.check.standard_no")}
          </p>
        )}
        {/* null with no rows is a grade the matrix has none for (the no_matrix line says it), not a folder that is not BTS. */}
        {check.standard == null && (check.rows?.length ?? 0) > 0 && <p className="text-xs text-slate-500">{t("variants.check.standard_none")}</p>}

        {blockingCount > 0 && (
          <div>
            <h5 className="text-xs font-semibold uppercase tracking-wider text-red-700">{t("variants.check.blocking_title")}</h5>
            <ul className="divide-y divide-slate-100">
              {emptyShort && (
                <li className="flex items-start gap-2 py-2">
                  <XCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
                  <p className="min-w-0 text-sm text-slate-700">{t("variants.check.all_empty", { count: check.planned?.count ?? 0 })}</p>
                </li>
              )}
              {shownBlocking.map((issue, i) => issueItem(issue, i, "red"))}
            </ul>
          </div>
        )}
        {warnings.length > 0 && (
          <div>
            <h5 className="text-xs font-semibold uppercase tracking-wider text-amber-700">{t("variants.check.warnings_title")}</h5>
            <ul className="divide-y divide-slate-100">{warnings.map((issue, i) => issueItem(issue, i, "amber"))}</ul>
          </div>
        )}
        {!issues.length && <p className="text-sm text-slate-500">{t("variants.check.no_issues")}</p>}

        {keepCandidates.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2.5">
            <h5 className="text-sm font-semibold text-slate-900">{t("variants.keep.title")}</h5>
            <p className="mt-0.5 text-xs text-slate-500">{t("variants.keep.text")}</p>
            <ul className="mt-2 space-y-2">
              {keepCandidates.map((uuid) => {
                const ref = refs.get(uuid);
                const label = t("variants.keep.switch", { question: t("variants.question_ref", { number: ref.number, code: ref.code }) });
                return (
                  <li key={uuid} className="flex items-center justify-between gap-3">
                    <span className="min-w-0 text-sm text-slate-700">
                      <span className="font-medium">{t("variants.question_ref", { number: ref.number, code: ref.code })}</span>
                      <span className="block text-xs text-slate-500">{t("variants.keep.label")}</span>
                    </span>
                    {keepBusy(uuid) && <Spinner className="ml-auto h-4 w-4" />}
                    <KeepOrderSwitch
                      checked={keep.has(uuid)}
                      label={label}
                      disabled={readOnly}
                      busy={keepBusy(uuid)}
                      onChange={(on) => onKeepOrder(uuid, on)}
                    />
                  </li>
                );
              })}
            </ul>
            {detail.status === "confirmed" && !readOnly && <p className="mt-2 text-xs text-amber-800">{t("variants.keep.unconfirms")}</p>}
          </div>
        )}
      </div>
    </section>
  );
});

export default VariantCheck;
